import React, { useEffect, useMemo, useState } from 'react';
import Image from 'components/image/Image';
import { useNavigate } from 'react-router';
import { IMe } from 'core/interface/me';
import { useSelector, useDispatch } from 'react-redux';
import { Pagination } from '@material-ui/core';
import { RootState } from 'redux/store';
import { SetColleaguesFilter } from '../../redux/slices/colleagues';
import { ColleaguesService } from 'services/colleagues/colleagues';
import Navbar from 'components/Navbar';
import { useTranslation } from 'react-i18next';
import { Colleague } from 'core/interface/colleagues';
import { COMPANY_DEFAULT_ROUTE } from 'core/constants/companyRoutes';
import { Role } from 'core/enum/role';
import { getColleagueRoleOptions } from 'core/constants/roleOptions';
import { IDropDownItem } from 'core/interface/dropDown';
import DropDown from 'components/Dropdown';
import Popup from 'components/shared/Popups/Popup/Index';
import ConfirmPopup from 'components/shared/Popups/Confirm/Index';

import './index.scss';

const getDisplayName = (colleague: Colleague): string => {
  const fullName = [colleague.first_name, colleague.last_name]
    .filter(Boolean)
    .join(' ')
    .trim();
  return fullName || colleague.name || colleague.email;
};

const AllColleagues = (): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const me = useSelector((state: IMe) => state.user).user;
  const dispatch = useDispatch();
  const { filters, colleagues } = useSelector(
    (state: RootState) => state.colleagues
  );
  const { page, limit, offset } = filters;
  const isAdmin = me?.role === Role.ADMIN;

  const [deleteTarget, setDeleteTarget] = useState<Colleague | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const roleOptions = useMemo(() => getColleagueRoleOptions(t), [t]);

  const getRoleItems = (currentRole: string): IDropDownItem[] => {
    if (roleOptions.some((item) => item.value === currentRole)) {
      return roleOptions;
    }

    return [
      { value: currentRole, to_show: t(currentRole) },
      ...roleOptions
    ];
  };

  const changeFilter = (field: string, value: string | number) => {
    dispatch(SetColleaguesFilter(field, value));
  };

  useEffect(() => {
    changeFilter('offset', (page ?? 1) * (limit ?? 6) - (limit ?? 6));
  }, [page, limit]);

  useEffect(() => {
    ColleaguesService.getColleagues({ limit, offset });
  }, [limit, offset]);

  const handleRoleChange = async (id: number, role: string) => {
    if (!role) return;
    await ColleaguesService.updateColleagueRole(id, role);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    await ColleaguesService.deleteColleague(deleteTarget.id);
    setIsDeleting(false);
    setDeleteTarget(null);
  };

  return (
    <>
      <Navbar />
      <div className="colleagues">
        <div
          className="back_buttons skillio-back-button"
          onClick={() => navigate(COMPANY_DEFAULT_ROUTE)}
        >
          <Image name="arrow-left.svg" />
          {t('Back')}
        </div>
        <h2 className="colleagues_title">{t('All colleagues')}</h2>
        <div className="colleagues_list">
          {colleagues?.results?.length ? (
            colleagues.results.map((el: Colleague) => (
              <div key={el.id} className="colleagues_list_card">
                <div className="colleagues_list_card_item">
                  <span className="colleagues_list_card_item_name">
                    {getDisplayName(el)}
                  </span>
                  <span className="colleagues_list_card_item_email">
                    {el.email}
                  </span>

                  {isAdmin ? (
                    <div className="colleagues_list_card_item_controls">
                      <div className="colleagues_list_card_item_role-field">
                        <span className="label">{t('Role')}</span>
                        <DropDown
                          className="colleagues_list_card_item_role-dropdown"
                          dropdownClassName="colleagues-role-select"
                          items={getRoleItems(el.role)}
                          placeholder={t('Role')}
                          defaultValue={el.role}
                          selectedValue={(value) =>
                            handleRoleChange(el.id, value)
                          }
                          disabled={el.id === me?.id}
                          showClose={false}
                        />
                      </div>
                      <button
                        onClick={() => setDeleteTarget(el)}
                        className="colleagues_list_card_item_controls_delete"
                        disabled={el.id === me?.id}
                      >
                        {t('Delete')}
                      </button>
                    </div>
                  ) : (
                    <div className="colleagues_list_card_item_status">
                      {t(el.role)}
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <p className="apps__filter__incoming-desc">
              {t('There are no colleagues.')}
            </p>
          )}
        </div>

        <Pagination
          className="pagination"
          count={Math.ceil((colleagues?.count as number) / (limit as number))}
          page={page}
          variant="outlined"
          shape="rounded"
          showFirstButton
          showLastButton
          onChange={(_, value: number) => {
            changeFilter('page', value);
          }}
        />
      </div>

      {deleteTarget && (
        <Popup title={t('Are you sure?')} onClose={() => setDeleteTarget(null)}>
          <ConfirmPopup
            approveTitle="Delete"
            description={t('Do you really want to delete this user?')}
            onCancel={() => setDeleteTarget(null)}
            t={t}
            approveLoading={isDeleting}
            onApprove={handleConfirmDelete}
          />
        </Popup>
      )}
    </>
  );
};

export default AllColleagues;
